/**
 * The `IdleTimer` detects when a user has been idle.
 *
 * When using the `IdleTimer` you can see how long the user has been idle
 * already and invoke a callback after a certain limit has been reached.
 */

export default class IdleTimer {
  private lastAction: Date | null = null;
  /** Interval after which to recheck the idle time limit. */
  private checkInterval: number = 1000;
  private intervalId: number | null = null;

  private eventHandler: (...args: any[]) => any;
  private listeningOptions = {
    capture: true,
    passive: true
  };

  /** Callback will be called after timeout. Return whether to continue calling it. */
  private callback: (() => boolean) | null = null;
  /** Time in minutes after which the callback should be called */
  private timeLimit: number = Infinity;

  constructor() {
    this.eventHandler = this.onUserAction.bind(this);
  }

  setCallback(callback: () => boolean, timeLimit: number) {
    this.timeLimit = timeLimit;
    this.callback = callback;
  }

  start() {
    this.lastAction = new Date();
    this.intervalId = window.setInterval(this.checkInactivity.bind(this), 1000);

    for (const event of ["mousemove", "click", "scroll", "keypress"]) {
      document.addEventListener(
        event,
        this.eventHandler,
        this.listeningOptions
      );
    }
  }

  stop() {
    window.clearInterval(<number>this.intervalId);
    this.intervalId = null;

    for (const event of ["mousemove", "click", "scroll", "keypress"]) {
      document.removeEventListener(
        event,
        this.eventHandler,
        this.listeningOptions
      );
    }
  }

  /**
   * Get time since last user action in milliseconds.
   */
  get idleTime() {
    if (!this.lastAction) {
      throw "IdleTimer must be started in order to be used.";
    }
    return new Date().getTime() - this.lastAction.getTime();
  }

  private checkInactivity() {
    if (this.callback && this.idleTime > this.timeLimit * 60 * 1000) {
      const keepCalling = this.callback();
      if (!keepCalling) {
        this.stop();
      }
    }
  }

  private onUserAction() {
    this.lastAction = new Date();
  }
}
